import React, { useState } from 'react';
import { LoadingAnimation, Text } from '@bufferapp/components';
import { CarouselCard } from '~/shared-components/Carousel';
import { UploadTypes } from '~/constants';
import CircularUploadIndicator from '~/composer/composer/components/progress-indicators/CircularUploadIndicator';
import { getLargeSafeImageUrl } from '~/story-group-composer/utils/SafeImage';
import { StoryCanvaDesignConfig } from '~/canva/constants';
import { useIsIntegrationUploading } from '~/integrations-bar/state/useIsIntegrationUploading';
import { useOldUploaderCallback } from '~/UploadZone/utils/useOldUplodaderCallback';
import { useStoryGroupNotifiers } from '~/story-group-composer/useStoryGroupNotifiers';
import { useDispatch } from 'react-redux';
import { actions } from '~/story-group-composer/reducer';
import UploadZone from '~/UploadZone';
import CarouselCardHover from '../CarouselCardHover';
import { carouselCardPropTypes } from '../../../utils/commonPropTypes';
import styles from './styles.css';

import { CoverImage, PlayIcon, StoryWrapper, UploadingVideo } from './styles';
import { openCanvaModal } from '../../../../canva/handlers';
import { openUploadcareModal } from '../../../../uploadcare/openUploadcareModal';
import {
  UploadcareIntegrations,
  UploadcareSources,
} from '../../../../uploadcare/constants';

const areEqual = (prevProps, nextProps) => {
  const { card, isOver, isDragging } = prevProps;
  const {
    card: cardNext,
    isOver: isOverNext,
    isDragging: isDraggingNext,
  } = nextProps;

  return (
    card.empty === cardNext.empty &&
    card.note === cardNext.note &&
    card.thumbnail_url === cardNext.thumbnail_url &&
    card.progress === cardNext.progress &&
    card.uploading === cardNext.uploading &&
    isOver === isOverNext &&
    isDragging === isDraggingNext
  );
};

const CardItem = React.memo(
  ({
    card,
    cardHeight,
    cardWidth,
    largeCards,
    uploadFormatsConfig,
    maxAttachableMediaCount,
    onAddNoteClick,
    onDeleteStoryClick,
    isOver,
    isDragging,
    translations,
  }) => {
    const [isHovering, setIsHovering] = useState(false);
    const { isIntegrationUploading, onUploadStarted, onUploadFinished } =
      useIsIntegrationUploading();
    const { oldUploadCallback } = useOldUploaderCallback({ randomId: true });
    const notifiers = useStoryGroupNotifiers();
    const dispatch = useDispatch();
    const removeNotifications = () => {
      dispatch(actions.hideError());
    };

    const serviceId = 'instagramStory';

    const onFileReady = (file, source) => {
      if (!file) return;

      oldUploadCallback(
        `${card.order}`,
        file,
        UploadTypes.MEDIA,
        notifiers,
        source
      );
    };

    const onError = ({ message }) =>
      notifiers.queueError?.({
        message,
      });

    const onEditCanvaImageClick = () => {
      openCanvaModal({
        onFileReady,
        designId: card.source.id,
        designConfig: StoryCanvaDesignConfig,
      });
    };

    const onEditButtonClick = () => {
      openUploadcareModal(
        serviceId,
        onFileReady,
        onError,
        UploadcareIntegrations.EDIT,
        onUploadStarted,
        onUploadFinished,
        UploadcareSources.EDIT,
        {
          url: card.asset_url,
          deleteCallback: () => onDeleteStoryClick(card),
        }
      );
    };

    return (
      <CarouselCard
        key={card.uploadTrackingId}
        card={card}
        cardHeight={cardHeight}
        cardWidth={cardWidth}
        largeCards={largeCards}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        isTarget={isOver}
      >
        {card.empty && (
          <UploadZone
            notifiers={notifiers}
            uploadDraftFile={oldUploadCallback}
            removeAllNotifications={removeNotifications}
            queueError={onError}
            classNames={styles}
            draftId={`${card.order}`}
            uploadFormatsConfig={uploadFormatsConfig}
            service={{
              maxAttachableImagesCount: maxAttachableMediaCount,
              canHaveMediaAttachmentType: () => true,
              name: serviceId,
            }}
            uploadType={UploadTypes.MEDIA}
            multiple
            disabled={isOver}
            showUploadMessage={!card.uploading && !isIntegrationUploading}
          />
        )}
        {((!card.empty && card.progress !== null && card.uploading) ||
          (card.empty && isIntegrationUploading)) && (
          <CircularUploadIndicator
            classNames={{ container: styles.container }}
            size={54}
            progress={card.progress || 0}
            showText
            finishingUpText="Finishing Upload..."
          />
        )}

        {card.thumbnail_url && (
          <StoryWrapper>
            <CoverImage
              src={getLargeSafeImageUrl(card.thumbnail_url)}
              isTarget={isOver}
            />
            {card.type === 'video' && <PlayIcon large={largeCards} />}
            {isHovering && !isDragging && (
              <CarouselCardHover
                card={card}
                translations={translations}
                onAddNoteClick={onAddNoteClick}
                onDeleteStoryClick={onDeleteStoryClick}
                onEditCanvaImageClick={onEditCanvaImageClick}
                onEditButtonClick={onEditButtonClick}
              />
            )}
          </StoryWrapper>
        )}

        {!card.empty && card.processing === true && (
          <UploadingVideo>
            <Text size="small">{translations.processingVideo}</Text>
            <LoadingAnimation marginTop="0" />
          </UploadingVideo>
        )}
      </CarouselCard>
    );
  },
  areEqual
);

CardItem.propTypes = {
  card: carouselCardPropTypes, // eslint-disable-line react/require-default-props,
  ...CarouselCardHover.propTypes,
};

CardItem.defaultProps = {
  card: {},
};

export default CardItem;
